import { format } from "date-fns";
import { useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Actions as FSS_REPORT_ACTIONS } from "../../store/actions/fssReportActions";
import { Actions as PAYROLL_ACTIONS } from "../../store/actions/payrollActions";

import { toDecimal } from "../common/validation/Validators";
import {
	ArrowDownTrayIcon,
	CurrencyPoundIcon,
	ExclamationTriangleIcon,
	PencilSquareIcon,
} from "@heroicons/react/20/solid";
import CopyID from "@/common/buttons/CopyID";
import Tooltip from "@/components/common/tooltip/Tooltip";

const round = (value) => {
	const round2 = toDecimal(2, 2, true);
	return round2(value);
};

const FssRow = ({
	fssReport,
	reportType,
	currency,
	disabled: isDisabled,
	downloadFSSReport,
	showF3InPortal,
	selectSubmissionForm,
	taxFormRows,
	openCISModal,
	saving,
	advisor,
	submitEPS,
}) => {
	const [view, setView] = useState(fssReport.view);
	const submitted = fssReport.status === "Success";
	const errorWarning = fssReport.status === "Error" || fssReport.status === "Failed";
	const disabled = saving || isDisabled || submitted;

	const downloadReport = () => {
		const input = {
			year: fssReport.year,
			name: reportType,
			org: fssReport.org,
		};

		switch (reportType) {
			case "p30":
			case "p32": {
				input.month = fssReport.month;
				break;
			}
			case "fs5": {
				// the year on FS5s seems buggy; so use the year from the period for safety
				input.year = fssReport.month.split("-")[0];
				// strip 0 from in front of FS5 month as we want only the month number
				input.month = fssReport.month.split("-")[1].replace(/^0+/, "");
				break;
			}
			case "p45":
			case "p60":
			case "fs3": {
				input.employee = fssReport.agreement;
				break;
			}
		}

		downloadFSSReport(input);
	};

	const toggleFS3Portal = (checked) => {
		const input = {
			year: fssReport.year,
			name: reportType,
			checked: checked,
		};

		switch (reportType) {
			case "fs5": {
				input.month = fssReport.month;
				break;
			}
			case "fs3": {
				input.employee = fssReport.idEmployee;
				input.agreement = fssReport.agreement;
				input.taxReportId = fssReport.id;
				break;
			}
		}

		showF3InPortal(input);
		setView(checked);
	};

	const reportName = (() => {
		if (fssReport.employeeName) return fssReport.employeeName;
		if (["fs5"].includes(reportType)) return `${format(new Date(`${fssReport.period}-01`), "MMM yyyy")}`;
		if (["p32", "p30"].includes(reportType)) {
			let month = parseInt(fssReport.period.split("-")[1]) + 3;
			let year = parseInt(fssReport.period.split("-")[0]);

			if (month > 12) {
				month = month - 12;
				year = year + 1;
			}

			const dateString = `${year}-${String(month).padStart(2, "0")}`;

			return `${format(new Date(dateString), "MMM yyyy")}`;
		}
		return fssReport.period;
	})();

	const baseReport = (() => {
		if (fssReport.fs3Summary) return fssReport.fs3Summary;
		if (reportType == "fs7") return fssReport.summary;
		return fssReport;
	})();

	return (
		<tr>
			<td>
				<CopyID id={fssReport.id} />
			</td>
			{advisor && <td>{fssReport.organisationName}</td>}
			<td>
				{reportType == "fs7" ? (
					<Link
						to={`/organisation/tax-forms/submission`}
						onClick={() => selectSubmissionForm(fssReport.year, reportType)}
						style={{ cursor: "pointer", color: "#ea611c" }}
					>
						{fssReport.year}
					</Link>
				) : (
					<>
						<a onClick={downloadReport}>
							{reportName}
							{fssReport.__imported ? <span className="help-block">Imported</span> : null}
						</a>
						{fssReport.paymentReference && (
							<Tooltip
								content={`This is the Payment Reference for this ${fssReport.type.toUpperCase()}`}
								className="text-muted"
							>
								{fssReport.paymentReference || ""}
							</Tooltip>
						)}
					</>
				)}
			</td>
			{taxFormRows?.map((row, index) => {
				let total = 0;
				if (!Array.isArray(row.value)) row.value = [row.value];

				row.value.forEach((value) => {
					total += parseFloat(baseReport?.[value]) || 0;
				});

				const addCIS = row.label == "CIS Deductions";

				return (
					<td key={row.label}>
						{currency}
						{round(total)}
						{addCIS && !total && !submitted ? (
							<button
								type="button"
								disabled={fssReport.__imported}
								onClick={() => openCISModal(fssReport)}
								className="btn btn-xs btn-default ml-2"
							>
								<CurrencyPoundIcon className="h-6 w-6 align-sub" /> Add CIS
							</button>
						) : null}
						{addCIS && total && !submitted ? (
							<button
								type="button"
								onClick={() => openCISModal(fssReport)}
								className="btn btn-xs btn-default ml-2"
							>
								<PencilSquareIcon className="h-6 w-6 align-sub" />
							</button>
						) : null}
					</td>
				);
			})}

			{reportType == "fs3" && (
				<td>
					<div
						title="Show in Portal"
						className="form-material"
						style={{ minWidth: 43, display: "inline-block" }}
					>
						<label className="css-input switch switch-sm switch-primary">
							<input
								type="checkbox"
								disabled={disabled}
								onClick={() => {
									if (!disabled) {
										toggleFS3Portal(!view);
									}
								}}
								checked={view}
							/>
							<span />
						</label>
						<div className="clearfix" />
					</div>
				</td>
			)}
			<td className="text-center">
				<div>
					{["p30"].includes(reportType) && (
						<>
							{errorWarning && (
								<Tooltip
									as="button"
									className="btn-xs btn btn-danger mr-2"
									content="Submission Failed!"
								>
									<ExclamationTriangleIcon className="h-6 w-6 align-middle" />
								</Tooltip>
							)}
							<button
								onClick={() => submitEPS(fssReport)}
								className={`btn btn-xs btn-${submitted ? "success" : "default"} mr-2`}
								disabled={disabled || submitted}
							>
								{submitted ? "Submitted" : "Submit EPS"}
							</button>
						</>
					)}
					{reportType == "fs7" && (
						<>
							<Link
								to={`/organisation/tax-forms/submission`}
								onClick={() => selectSubmissionForm(fssReport.year, reportType)}
								className="btn btn-xs btn-default"
								style={{ marginRight: 5 }}
							>
								Submit Using FSSXpress™
							</Link>
							<a
								className="btn btn-xs btn-default"
								type="button"
								title="SS Lodgement File Download"
								target="_blank"
								download={`ssc${fssReport.year}.pdf`}
								href={`https://api.buddy.hr/v2/fss/ss/${fssReport.year}`}
							>
								SS
							</a>
							<a
								className="btn btn-xs btn-default"
								type="button"
								title="F3 Lodgement File Download"
								target="_blank"
								download={`ssc${fssReport.year}.pdf`}
								href={`https://api.buddy.hr/v2/fss/f3/${fssReport.year}`}
							>
								F3
							</a>
						</>
					)}
					<a
						className="btn btn-xs btn-default"
						onClick={() => {
							if (!fssReport.__imported) {
								downloadReport();
							}
						}}
						disabled={fssReport.__imported}
					>
						<ArrowDownTrayIcon className="h-5 w-5 align-middle" />
					</a>
				</div>
			</td>
		</tr>
	);
};

const mapStateToProps = (state, { taxFormRows, fssReport }) => {
	const rows = state.jurisdiction?.taxFormRows;
	const cisSaving = state.taxForms?.cisDeductionsSaving?.includes(fssReport?.id);
	const saving = cisSaving;

	return {
		taxFormRows: taxFormRows || rows,
		saving,
		currency: state.company?.settings?.currency,
		disabled: state?.permissions?.my?.permissions?.taxForms == "Read",
	};
};

const mapDispatchToProps = (dispatch) => ({
	downloadFSSReport: (data) => dispatch(FSS_REPORT_ACTIONS.downloadFSSReport(data)),
	showF3InPortal: (data) => dispatch(FSS_REPORT_ACTIONS.showF3InPortal(data)),
	selectSubmissionForm: (year, type) => dispatch(FSS_REPORT_ACTIONS.selectSubmissionForm(year, type)),
	submitEPS: (fssReport) => dispatch(PAYROLL_ACTIONS.submitEPS(fssReport)),
	openCISModal: (report) => dispatch(FSS_REPORT_ACTIONS.openCISModal(report)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FssRow);
